import { Cake } from 'lucide-react';
import {
  birthdayCountdown,
  birthdayHeadline,
  fromDateKey,
  shortDate,
  weekdayName,
  type UpcomingBirthday,
} from '@awwyeah/shared';
import { MemberAvatar } from './MemberBits';
import type { MemberView } from '../members';

/**
 * Upcoming birthdays surfaced in the calendar (design Screen C · 9). They live in the
 * `household` doc; ages are computed, never typed. Non-members get a neutral avatar.
 */
export function UpcomingBirthdays({
  entries,
  members,
  now,
}: {
  entries: readonly UpcomingBirthday[];
  members: ReadonlyMap<string, MemberView>;
  now: Date;
}): JSX.Element | null {
  if (entries.length === 0) return null;
  const [next, ...rest] = entries;
  if (!next) return null;

  return (
    <section className="flex flex-col gap-2 pt-2" data-testid="upcoming-birthdays">
      <div className="flex items-center gap-2">
        <Cake className="h-4 w-4 text-accent" aria-hidden="true" />
        <h2 className="text-[11px] font-semibold uppercase tracking-[0.08em] text-fg-3">
          Birthdays
        </h2>
      </div>

      <div className="flex items-center gap-3 rounded-sm border border-line bg-surface px-3 py-3">
        <MemberAvatar
          member={next.birthday.memberId ? members.get(next.birthday.memberId) : undefined}
          size="lg"
        />
        <div className="min-w-0 flex-1">
          <p className="truncate text-base font-semibold">{birthdayHeadline(next)}</p>
          <p className="truncate text-xs text-fg-3">
            {weekdayName(fromDateKey(next.date))}, {shortDate(fromDateKey(next.date), now)} ·{' '}
            {birthdayCountdown(next)}
          </p>
        </div>
        <span className="shrink-0 rounded-full bg-raised px-2 py-1 text-[11px] font-semibold text-fg-2">
          Next
        </span>
      </div>

      {rest.length > 0 && (
        <ul className="flex flex-col gap-px overflow-hidden rounded-sm border border-line">
          {rest.slice(0, 3).map((entry) => (
            <li
              key={entry.birthday.id}
              className="flex items-center gap-3 bg-surface px-3 py-2 text-sm"
            >
              <MemberAvatar
                member={entry.birthday.memberId ? members.get(entry.birthday.memberId) : undefined}
                size="md"
              />
              <span className="min-w-0 flex-1 truncate">{birthdayHeadline(entry)}</span>
              <span className="shrink-0 font-mono text-[11px] text-fg-3">{entry.daysUntil}d</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
